import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { IafItemSvc } from '../../services/platformApi';
import {
  SUGGESTED_INVITE_TEMPLATE,
  SUGGESTED_REMINDER_TEMPLATE,
  TEMPLATE_NAMES,
  type TemplateName,
} from './templateConstants';
import './InviteTemplateManagementPage.css';

// ---------------------------------------------------------------------------
// Collection config — drafts stored in IafItemSvc, NOT Passport
// ---------------------------------------------------------------------------

const DRAFTS_COLLECTION = {
  NAME: 'Invite Template Drafts',
  SHORT_NAME: 'invite_template_drafts',
  USER_TYPE: 'invite_template_drafts',
  DESCRIPTION: 'Reusable invite email template drafts for deployment to training apps',
};

interface TemplateDraft {
  _id: string;
  _name: string;
  _title: string;
  _message: string;
  _format: string;
  _locale?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InviteTemplateManagementPage() {
  const { accessToken } = useAuth();
  const { selectedProject } = useApp();

  // Build a ctx scoped to the workspace namespace (same as other DAM collections)
  const getCtx = useCallback(() => {
    const namespaces = (selectedProject as any)?._namespaces || [];
    return { authToken: accessToken || '', _namespaces: namespaces };
  }, [accessToken, selectedProject]);

  const [templates, setTemplates] = useState<TemplateDraft[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateDraft | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create-new state
  const [isCreating, setIsCreating] = useState(false);
  const [createName, setCreateName] = useState<TemplateName>('USER_GROUP_INVITE');

  // Editor state
  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editLocale, setEditLocale] = useState('en');

  // Preview — renders the HTML directly; ${invite_link} gets a placeholder
  const [showPreview, setShowPreview] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  // Collection state
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [collectionReady, setCollectionReady] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);

  // ---- Collection helpers -----------------------------------------------

  const findCollection = useCallback(async (): Promise<string | null> => {
    const ctx = getCtx();
    const resp = await IafItemSvc.getNamedUserItems({
      query: { _userType: DRAFTS_COLLECTION.USER_TYPE }
    }, ctx);

    if (resp?._list?.length) {
      return resp._list[0]._userItemId as string;
    }
    return null;
  }, [getCtx]);

  const createCollection = useCallback(async () => {
    setIsCreatingCollection(true);
    setCollectionError(null);
    try {
      const ctx = getCtx();
      const createResp = await IafItemSvc.createNamedUserItems([{
        _name: DRAFTS_COLLECTION.NAME,
        _shortName: DRAFTS_COLLECTION.SHORT_NAME,
        _description: DRAFTS_COLLECTION.DESCRIPTION,
        _userType: DRAFTS_COLLECTION.USER_TYPE,
        _namespaces: ctx._namespaces || [],
      }], 'NamedUserCollection', ctx);

      const id = (createResp?._list?.[0]?._userItemId as string) || null;
      if (id) {
        setCollectionId(id);
        setCollectionReady(true);
        setSuccessMessage('Drafts collection created!');
      } else {
        setCollectionError('Collection was created but no ID was returned. Try refreshing.');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setCollectionError(`Failed to create drafts collection: ${msg}`);
    } finally {
      setIsCreatingCollection(false);
    }
  }, [getCtx]);

  const ensureCollection = useCallback(async (): Promise<string | null> => {
    if (collectionId) return collectionId;
    try {
      const id = await findCollection();
      if (id) {
        setCollectionId(id);
        setCollectionReady(true);
        return id;
      }
      setCollectionReady(false);
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setCollectionError(`Failed to check drafts collection: ${msg}`);
      setCollectionReady(false);
      return null;
    }
  }, [collectionId, findCollection]);

  // ---- Fetch drafts -------------------------------------------------------

  const fetchTemplates = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    setCollectionError(null);

    try {
      const cId = await ensureCollection();
      if (!cId) {
        setTemplates([]);
        setIsLoading(false);
        return;
      }

      const ctx = getCtx();
      const resp = await IafItemSvc.getRelatedItems(
        cId,
        {},
        ctx,
        { page: { _pageSize: 100 } }
      );

      const drafts: TemplateDraft[] = (resp?._list || []).map(item => ({
        _id: item._id,
        _name: String(item._name ?? item.name ?? ''),
        _title: String(item._title ?? ''),
        _message: String(item._message ?? ''),
        _format: String(item._format ?? 'text/html'),
        _locale: item._locale as string | undefined,
        createdAt: item.createdAt as string | undefined,
        updatedAt: item.updatedAt as string | undefined,
      }));

      setTemplates(drafts);

      if (drafts.length > 0 && !selectedTemplate) {
        selectTemplate(drafts[0]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to fetch template drafts: ${msg}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, ensureCollection, getCtx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ---- Select / Edit ------------------------------------------------------

  const selectTemplate = (template: TemplateDraft) => {
    setSelectedTemplate(template);
    setEditTitle(template._title || '');
    setEditMessage(template._message || '');
    setEditLocale(template._locale || 'en');
    setIsCreating(false);
    setShowPreview(false);
  };

  const saveTemplate = async () => {
    if (!selectedTemplate || !accessToken) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const cId = await ensureCollection();
      if (!cId) throw new Error('Drafts collection not found. Use the button above to create it first.');
      const ctx = getCtx();

      await IafItemSvc.updateRelatedItem(cId, selectedTemplate._id, {
        _name: selectedTemplate._name,
        _title: editTitle,
        _message: editMessage,
        _format: 'text/html',
        _locale: editLocale,
        updatedAt: new Date().toISOString(),
      }, ctx);

      setSuccessMessage('Template draft saved!');
      await fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to save template: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Create -------------------------------------------------------------

  const startCreating = () => {
    setSelectedTemplate(null);
    setIsCreating(true);
    setCreateName('USER_GROUP_INVITE');
    setEditTitle('Welcome to Twinit Developer Training');
    setEditMessage(SUGGESTED_INVITE_TEMPLATE);
    setEditLocale('en');
    setShowPreview(false);
  };

  const handleCreateNameChange = (name: TemplateName) => {
    setCreateName(name);
    if (name === 'USER_GROUP_INVITE') {
      setEditTitle('Welcome to Twinit Developer Training');
      setEditMessage(SUGGESTED_INVITE_TEMPLATE);
    } else {
      setEditTitle('Reminder: Invitation to Twinit Developer Training');
      setEditMessage(SUGGESTED_REMINDER_TEMPLATE);
    }
  };

  const createTemplate = async () => {
    if (!accessToken) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const cId = await ensureCollection();
      if (!cId) throw new Error('Drafts collection not found. Use the button above to create it first.');
      const ctx = getCtx();

      await IafItemSvc.createRelatedItems(cId, [{
        _name: createName,
        _title: editTitle,
        _message: editMessage,
        _format: 'text/html',
        _locale: editLocale,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }], ctx);

      setSuccessMessage(`Template draft "${createName}" saved!`);
      setIsCreating(false);
      await fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to create template draft: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Delete -------------------------------------------------------------

  const deleteTemplate = async () => {
    if (!selectedTemplate || !accessToken) return;
    if (!window.confirm(`Delete the "${selectedTemplate._name}" draft?`)) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const cId = await ensureCollection();
      if (!cId) throw new Error('Drafts collection not found. Use the button above to create it first.');
      const ctx = getCtx();

      await IafItemSvc.deleteRelatedItem(cId, selectedTemplate._id, ctx);

      setSuccessMessage('Template draft deleted.');
      setSelectedTemplate(null);
      await fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to delete template: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Load starter -------------------------------------------------------

  const loadSuggestedLayout = () => {
    const isReminder =
      (isCreating && createName === 'USER_GROUP_INVITE_REMINDER') ||
      (!isCreating && selectedTemplate?._name === 'USER_GROUP_INVITE_REMINDER');
    setEditMessage(isReminder ? SUGGESTED_REMINDER_TEMPLATE : SUGGESTED_INVITE_TEMPLATE);
  };

  // ---- Preview ------------------------------------------------------------

  const getPreviewHtml = useCallback(() => {
    // Substitute all standard Passport variables with representative sample
    // values so the preview renders realistically.
    return editMessage
      .replace(/\$\{base_url\}/g, 'https://sandbox-api.invicara.com')
      .replace(/\$\{subject\}/g, 'You\'ve been invited to Twinit Academy Training Application')
      .replace(/\$\{body_header\}/g, 'Twinit Academy Training Application')
      .replace(/\$\{name\}/g, 'Acme Corp Developer Training')
      .replace(/\$\{body_content\}/g,
        '<p>In the next few minutes you will be receiving a few invitations to various Twinit resources including Twinit Academy. ' +
        'I&rsquo;d like to quickly provide you a summary of all the resources that will be available to you. ' +
        'If you&rsquo;d like to schedule a call to walk through and receive an introduction to any or all of these resources please let me know.</p>')
      .replace(/\$\{inviter_name\}/g, 'Invicara Developer Team')
      .replace(/\$\{invite_link\}/g, 'https://sandbox.invicara.com/console#preview-placeholder')
      .replace(/\$host/g, 'sandbox.invicara.com');
  }, [editMessage]);

  useEffect(() => {
    if (showPreview && previewIframeRef.current) {
      const iframe = previewIframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(getPreviewHtml());
        doc.close();
      }
    }
  }, [showPreview, getPreviewHtml]);

  // ---- Derived ------------------------------------------------------------

  const existingNames = new Set(templates.map(t => t._name));
  const missingTemplates = TEMPLATE_NAMES.filter(n => !existingNames.has(n));
  const isEditorActive = isCreating || selectedTemplate != null;
  const editorLabel = isCreating ? createName : selectedTemplate?._name || '';

  // ---- Render -------------------------------------------------------------

  return (
    <div className="invite-template-management">
      {/* Header */}
      <div className="itm-header">
        <div className="itm-title">
          <i className="fas fa-envelope-open-text"></i>
          <h1>Invite Email Templates</h1>
        </div>
        <p className="itm-subtitle">
          Design and save invite email HTML. The top section uses standard Passport variables
          (<code>{'${body_header}'}</code>, <code>{'${name}'}</code>, <code>{'${body_content}'}</code>,
          <code>{'${inviter_name}'}</code>, <code>{'${base_url}'}</code>, <code>{'${invite_link}'}</code>)
          so any caller (Console App, onboarding workflow) can fill in their own values.
          The training resources section is hardcoded HTML and always appears regardless of who triggered the invite.
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="itm-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="itm-message success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      {/* Collection status */}
      {!collectionReady && !isLoading && (
        <div className="itm-collection-banner">
          <div className="itm-collection-banner-content">
            <i className="fas fa-database"></i>
            <div>
              <strong>Drafts collection not found</strong>
              <p>A settings collection is needed to store template drafts. Create it to get started.</p>
              {collectionError && <p className="itm-collection-banner-error">{collectionError}</p>}
            </div>
          </div>
          <button
            className="itm-btn itm-btn-primary"
            onClick={createCollection}
            disabled={isCreatingCollection}
          >
            <i className="fas fa-plus-circle"></i>
            {isCreatingCollection ? 'Creating...' : 'Create Drafts Collection'}
          </button>
        </div>
      )}

      <div className="itm-layout">
        {/* ---- Sidebar ---- */}
        <div className="itm-sidebar">
          <h3>Saved Drafts</h3>

          {isLoading && <div className="itm-loading">Loading...</div>}

          {templates.map(t => (
            <div
              key={t._id}
              className={`itm-template-card ${selectedTemplate?._id === t._id && !isCreating ? 'active' : ''}`}
              onClick={() => selectTemplate(t)}
            >
              <div className="itm-template-name">{t._name}</div>
              <div className="itm-template-title">{t._title}</div>
            </div>
          ))}

          {templates.length === 0 && !isLoading && (
            <div className="itm-empty">No template drafts saved yet. Create one to get started.</div>
          )}

          <button
            className={`itm-btn itm-btn-create ${isCreating ? 'active' : ''}`}
            onClick={startCreating}
          >
            <i className="fas fa-plus"></i> Create New Draft
          </button>

          {missingTemplates.length > 0 && !isCreating && (
            <div className="itm-sidebar-hint">
              Suggested: {missingTemplates.map(n => <code key={n}>{n}</code>)}
            </div>
          )}
        </div>

        {/* ---- Editor ---- */}
        <div className="itm-editor">
          {isEditorActive ? (
            <>
              <div className="itm-editor-header">
                <h3>
                  {isCreating && <span className="itm-badge-new">NEW</span>}
                  {editorLabel}
                </h3>
                <div className="itm-editor-actions">
                  <button className="itm-btn itm-btn-outline" onClick={loadSuggestedLayout}>
                    <i className="fas fa-magic"></i> Load Starter HTML
                  </button>
                  <button
                    className="itm-btn itm-btn-secondary"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    <i className={`fas fa-${showPreview ? 'code' : 'eye'}`}></i>
                    {showPreview ? 'Editor' : 'Preview'}
                  </button>
                  <button
                    className="itm-btn itm-btn-primary"
                    onClick={isCreating ? createTemplate : saveTemplate}
                    disabled={isSaving}
                  >
                    <i className="fas fa-save"></i>
                    {isSaving ? 'Saving...' : isCreating ? 'Save Draft' : 'Save'}
                  </button>
                  {!isCreating && selectedTemplate && (
                    <button
                      className="itm-btn itm-btn-secondary"
                      onClick={deleteTemplate}
                      disabled={isSaving}
                      style={{ color: '#fca5a5' }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Template name selector (only when creating) */}
              {isCreating && (
                <div className="itm-field">
                  <label>Template Name</label>
                  <div className="itm-name-selector">
                    {TEMPLATE_NAMES.map(n => (
                      <button
                        key={n}
                        className={`itm-name-option ${createName === n ? 'active' : ''}`}
                        onClick={() => handleCreateNameChange(n)}
                      >
                        <code>{n}</code>
                        {existingNames.has(n) && <span className="itm-exists-badge">exists</span>}
                      </button>
                    ))}
                  </div>
                  {existingNames.has(createName) && (
                    <span className="itm-hint itm-hint-warn">
                      <i className="fas fa-exclamation-triangle"></i> A draft with this name already exists. You can still save another version.
                    </span>
                  )}
                </div>
              )}

              <div className="itm-field">
                <label>Email Subject (plain text)</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="Welcome to Twinit Developer Training"
                />
                <span className="itm-hint">This becomes the email subject line. Keep it plain text — no HTML.</span>
              </div>

              <div className="itm-field">
                <label>Locale</label>
                <input
                  type="text"
                  value={editLocale}
                  onChange={e => setEditLocale(e.target.value)}
                  placeholder="en"
                  style={{ maxWidth: '100px' }}
                />
              </div>

              {!showPreview ? (
                <div className="itm-field itm-field-grow">
                  <label>
                    Template Body (HTML with Velocity variables)
                    <span className="itm-hint" style={{ marginLeft: 8 }}>
                      Use standard Passport variables in the top section; hardcode training content below.
                    </span>
                  </label>
                  <textarea
                    className="itm-code-editor"
                    value={editMessage}
                    onChange={e => setEditMessage(e.target.value)}
                    spellCheck={false}
                  />
                </div>
              ) : (
                <div className="itm-preview-section">
                  <div className="itm-preview-frame-container">
                    <h4>
                      Email Preview
                      <span style={{ fontWeight: 'normal', fontSize: '0.85em', marginLeft: 8, color: '#888' }}>
                        (standard Passport variables filled with sample values)
                      </span>
                    </h4>
                    <iframe
                      ref={previewIframeRef}
                      className="itm-preview-frame"
                      title="Template Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="itm-empty-editor">
              <i className="fas fa-envelope-open-text"></i>
              <p>Select a draft to edit, or create a new one</p>
            </div>
          )}
        </div>
      </div>

      {/* Info panel */}
      <div className="itm-info-panel">
        <h4><i className="fas fa-info-circle"></i> How it works</h4>
        <div className="itm-info-grid">
          <div>
            <h5>Design principle — hybrid template</h5>
            <p>The <strong>top section</strong> uses standard Passport Velocity variables filled by any caller
            (Console App, onboarding workflow, CCRA):</p>
            <ul>
              <li><code>{'${base_url}'}</code> — API origin (logo src)</li>
              <li><code>{'${subject}'}</code> — email subject / page title</li>
              <li><code>{'${body_header}'}</code> — large heading (app name)</li>
              <li><code>{'${name}'}</code> — subtitle (project / group name)</li>
              <li><code>{'${body_content}'}</code> — opening paragraph / greeting</li>
              <li><code>{'${inviter_name}'}</code> — person or system sending the invite</li>
              <li><code>{'${invite_link}'}</code> — unique per-user accept URL (required)</li>
            </ul>
            <p>The <strong>training resources section</strong> (Academy links, support contacts, next-steps)
            is hardcoded HTML and always appears regardless of who triggered the invite.</p>
          </div>
          <div>
            <h5>Deployment</h5>
            <p>Drafts saved here are automatically deployed to each new training application during the
            <strong> Developer Onboarding Workflow</strong>. The workflow reads this collection and pushes
            the HTML to Passport as the <code>USER_GROUP_INVITE</code> (and <code>USER_GROUP_INVITE_REMINDER</code>)
            notification template scoped to the new app.</p>
            <p>To update an already-deployed template, edit the draft here and re-run the workflow
            for that application — the deploy step is idempotent and will update in place.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
